import {
  ButtonPrimary,
  SelectInputMultiple,
} from '@skf/bt-wind-rail-commonlibrary';
import { IOption } from '@skf/bt-wind-rail-commonlibrary/dist/components/form/types';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useAssignUserRoles } from '../../../../hooks/useAssignUserRoles';
import { useLoadingScreen } from '../../../../hooks/useLoadingScreen';
import { usePathname } from '../../../../hooks/usePathname';
import { useToast } from '../../../../hooks/useToast';
import { UserManagementRoute } from '../../../../routes/routes';
import { isValidResponse } from '../../../../services/responseService';
import { orderByAlphabetical } from '../../../../services/stringServices';
import { clearNotificationsMessage } from '../../../../store/notifications/actions';
import { INotificationsState } from '../../../../store/notifications/store';
import { fetchRolesInfo } from '../../../../store/roles/actions';
import { IRolesState } from '../../../../store/roles/store';
import {
  BasicIndustryRole,
  BasicPortalRoleMultiple,
  DistributorApiDTO,
  IIndustryRoleLabelInfo,
  ILabelInfo,
  ImxUserData,
  IMXUserInformation,
  IRoleData,
  SelectedRoleInfo,
  UserRole,
} from '../../../../store/roles/types';
import { StoreState } from '../../../../store/store';
import {
  clearUsersError,
  getUsersRolesInformation,
  postDistributorsAccount,
  postIMXDataMapApplication,
  postUsersRoles,
} from '../../../../store/users/actions';
import { IUsersState } from '../../../../store/users/store';
import {
  SingleUsersRolesInformation,
  User,
} from '../../../../store/users/types';
import {
  isSelectedRoleInfoEqual,
  mapApiUserRoleFromSelectedRoleInfo,
} from './UserManagementAssignRole.mapper';
import style from './UserManagementAssignRoles.module.scss';
import UserManagementAssignRolesDataTable from './rolesDataTable/UserManagementAssignRolesDataTable';
import { UserManagementAssignRolesSelector } from './rolesSelector/UserManagementAssignRolesSelector';
import {
  groupByIndustries,
  mapDistributorDataToIOption,
  mapUserRoleInformativeToIndustryRoleLabelInfo,
  mapUserRoleInformativeToSelectedRoleInfosNoDuplicates,
  mapUserRoleInformativeToSelectedRoles,
} from './rolesSelector/UserManagementAssignRolesSelector.mapper';

interface IUserManagementAddProps {}

const UserManagementAssignRoles: React.FunctionComponent<IUserManagementAddProps> = ({}) => {
  const dispatch = useDispatch();
  const { setLoading } = useLoadingScreen();
  const { assignUserRoles } = useAssignUserRoles();
  const { setPathname } = usePathname();
  const { addToast } = useToast();
  const { t } = useTranslation();

  const { rolesInfo }: IRolesState = useSelector<StoreState, IRolesState>(
    (state) => state.roles
  );
  const { status, message, user, users }: IUsersState = useSelector<
    StoreState,
    IUsersState
  >((state) => state.users);
  const { notification }: INotificationsState = useSelector<
    StoreState,
    INotificationsState
  >((state) => state.notifications);

  const [selectedRoleInfo, setSelectedRoleInfo] = useState<SelectedRoleInfo[]>(
    []
  );
  const [apiUserRole, setApiUserRole] = useState<UserRole[]>([]);
  const [currentIndustries, setCurrentIndustries] = useState<
    BasicIndustryRole[]
  >([]);
  const [isFetching, setFetching] = useState<boolean>(false);
  const [currentPermissions, setCurrentPermissions] = useState<
    BasicPortalRoleMultiple[]
  >([]);

  const [imxData, setImxData] = useState<ImxUserData>({
    userName: '',
    imxCompanyName: [],
    imxDbName: [],
  });

  const [industryRoleLabels, setIndustryRoleLabels] = useState<
    IIndustryRoleLabelInfo[]
  >([]);

  const [selectedRoles, setSelectedRoles] = useState<Record<string, string[]>>(
    {}
  );

  const [distributorData, setDistributorData] = useState<DistributorApiDTO[]>(
    []
  );

  const [distributorDataSelected, setDistributorDataSelected] = useState<
    DistributorApiDTO[]
  >([]);

  const [
    selectedDistributorIOptions,
    setSelectedDistributorIOptions,
  ] = useState<IOption[]>([]);

  useEffect(() => {
    if (rolesInfo && user) return setLoading(false);
  }, [rolesInfo, user]);

  useEffect(() => {
    const fetchData = async () => {
      await dispatch(fetchRolesInfo());
      if (assignUserRoles.length === 1) {
        await dispatch(
          getUsersRolesInformation(
            assignUserRoles[0].userName,
            assignUserRoles[0].userEmail
          )
        );
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    if (assignUserRoles.length > 0) return;
    setPathname(UserManagementRoute.path);
  }, [assignUserRoles]);

  useEffect(() => {
    const currentIndustries = groupByIndustries(selectedRoleInfo);

    setCurrentIndustries(currentIndustries);
  }, [selectedRoleInfo]);

  useEffect(() => {
    const industryCodes = currentIndustries.map((x) => x.industryCode);
    const filteredPermissions = currentPermissions.filter(
      (x) => industryCodes.indexOf(x.industry.industryCode) !== -1
    );
    setCurrentPermissions(filteredPermissions);
  }, [currentIndustries]);

  useEffect(() => {
    const _isValidResponse = isValidResponse(status);

    if (isFetching && _isValidResponse) {
      addToast({
        status: 'ok',
        message: t('User_assignment_request_completed'),
      });
      dispatch(clearUsersError());

      return setPathname(UserManagementRoute.path);
    }

    if (!status || !message) return;
    addToast({
      status: 'danger',
      message: message || `Error ${status}`,
    });

    dispatch(clearUsersError());
    setLoading(false);
    setFetching(false);
  }, [status, message, isFetching]);

  useEffect(() => {
    if (notification?.message) {
      setLoading(false);
      setFetching(false);
      dispatch(clearNotificationsMessage());
    }
  }, [notification?.message]);

  useEffect(() => {
    setApiUserRole(
      mapApiUserRoleFromSelectedRoleInfo(
        selectedRoleInfo,
        currentPermissions,
        imxData
      )
    );
  }, [currentPermissions, selectedRoleInfo, imxData]);

  useEffect(() => {
    if (user) {
      let labels: IIndustryRoleLabelInfo[] = [];

      // Handle user roles and distributor data
      if (user.userRoles.length > 0 && rolesInfo !== undefined) {
        setSelectedRoleInfo(
          mapUserRoleInformativeToSelectedRoleInfosNoDuplicates(user.userRoles)
        );
        setSelectedRoles(mapUserRoleInformativeToSelectedRoles(user.userRoles));

        const roleLabels = mapUserRoleInformativeToIndustryRoleLabelInfo(
          user.userRoles,
          rolesInfo
        );

        labels.push(...roleLabels);

        if (user.distributors) {
          labels.push({
            industryCode: 3,
            industryPortal: 'remanXPDistributors',
            values: [
              ...new Set(user.distributors.map((x) => x.distributorName)),
            ],
          });
        }
        setDistributorDataSelected(user.distributors ?? []);

        const existingRoles = selectIndustryBatch(
          roleLabels.map((x) => {
            return {
              industry: x.industryCode,
              portal: x.industryPortal,
              roleNames: x.values.map<IOption>((y) => {
                return {
                  label: y,
                  labelToShow: y,
                  value: y,
                };
              }),
            };
          })
        );

        setCurrentPermissions(existingRoles);
      }

      // Handle IMX data and labels
      if (user.imxUserData) {
        setImxData(user.imxUserData);
        if (
          labels.findIndex((x) => x.industryPortal === 'Diagnostic_Unit') === -1
        ) {
          labels.push({
            industryCode: -1,
            industryPortal: 'Diagnostic_Unit',
            values: [...new Set(user.imxUserData.imxCompanyName)],
          });
        }
        if (labels.findIndex((x) => x.industryPortal === 'Database') === -1) {
          labels.push({
            industryCode: -1,
            industryPortal: 'Database',
            values: [...new Set(user.imxUserData.imxDbName)],
          });
        }
      } else {
        setImxData({
          userName: '',
          imxCompanyName: [],
          imxDbName: [],
        });
      }
      setIndustryRoleLabels(labels);

      // Handle distributor data
      if (rolesInfo && rolesInfo.find((x) => x.industryCode === 3)) {
        const remanData = rolesInfo.find((x) => x.industryCode === 3)
          ?.remanXpData;
        if (
          remanData &&
          user.userRoles.find((y) => y.portalCode === remanData.portalReference)
        ) {
          setDistributorData(remanData.distributorInfo);
        }
      }
      if (user.distributors) {
        setSelectedDistributorIOptions(
          user.distributors.map((x) => {
            return {
              label: x.distributorName,
              labelToShow: x.distributorName,
              value: x.distributorAccount,
            };
          })
        );
      }
    }
  }, [user, rolesInfo]);

  const _onDeleteRole = (_roleInfo: SelectedRoleInfo) => {
    setSelectedRoleInfo(
      selectedRoleInfo.filter(
        (_selectedRoleInfo) =>
          !isSelectedRoleInfoEqual(_selectedRoleInfo, _roleInfo)
      )
    );
  };

  const onClickCancel = () => {
    setPathname(UserManagementRoute.path);
  };

  const onClickSubmit = () => {
    const filteredUserRoles = apiUserRole.filter(
      (el) => el.industryText !== ''
    );

    if (assignUserRoles.length === 0) {
      addToast({ status: 'danger', message: t('noUsersSelected') });
      return;
    }

    assignUserRoles.forEach((user) => {
      imxData.userName = user.userName;

      const BTRailRoleObject = filteredUserRoles.find(
        (x) => x.industryText == 'Rail Business Transformation'
      );
      const BTWindRoleObject = filteredUserRoles.find(
        (x) => x.industryText == 'Wind Business Transformation'
      );

      const BTRailRole =
        BTRailRoleObject?.roleDisplayName || BTRailRoleObject?.roleName;
      const BTWindRole =
        BTWindRoleObject?.roleDisplayName || BTWindRoleObject?.roleName;

      if (BTRailRole && BTWindRole && BTRailRole !== BTWindRole) {
        if (BTRailRole !== 'No Access' && BTWindRole !== 'No Access') {
          addToast({
            status: 'danger',
            message: t('wrongBTRoles'),
          });
          return;
        }
      }

      const userRolesFiltered = filteredUserRoles.filter(
        (x) => x.roleName !== 'No Access'
      );

      const singleUserRolesInformation: SingleUsersRolesInformation = {
        user,
        usersRoles: userRolesFiltered,
      };

      const imxUserInformationDTO: IMXUserInformation = {
        imxUserData: imxData,
        singleUserRole: singleUserRolesInformation,
      };

      dispatch(postUsersRoles(singleUserRolesInformation));
      dispatch(postIMXDataMapApplication(imxUserInformationDTO));
      dispatch(
        postDistributorsAccount({
          userEmail: user.userEmail,
          distributorAccounts: distributorDataSelected.map(
            (x) => x.distributorAccount
          ),
        })
      );
    });

    setFetching(true);
    setLoading(true);
  };

  const onSelectIndustryRole = (
    industry: number,
    portal: string,
    ogPortalName: string,
    roleNames: IOption[],
    labelIndex: number
  ) => {
    if (rolesInfo !== undefined) {
      const labelData: ILabelInfo[] = [];
      const roleData: IRoleData[] = roleNames.map<IRoleData>((x) => {
        return {
          roleName: x.value.toString(),
          rolePriority:
            rolesInfo
              .find((y) => y.industryCode === industry)
              ?.industryRoles.find(
                (role) => role.roleName === x.value.toString()
              )?.rolePriority || 0,
          roleDisplayName:
            rolesInfo
              .find((y) => y.industryCode === industry)
              ?.industryRoles.find(
                (role) => role.roleName === x.value.toString()
              )?.roleDisplayName || x.value.toString(),
        };
      });

      if (
        currentPermissions.findIndex(
          (x) => x.industry.industryCode === industry && x.portal === portal
        ) === -1
      ) {
        const newPermission: BasicPortalRoleMultiple = {
          industry: {
            industryCode: industry,
            industryOrder: 0,
            industryText:
              rolesInfo.find((y) => y.industryCode === industry)
                ?.industryName || '',
          },
          portal: portal,
          roleData: roleData,
          imxUserData: { userName: '', imxCompanyName: [], imxDbName: [] },
        };
        setCurrentPermissions([...currentPermissions, newPermission]);
      } else {
        const existingPermissions: BasicPortalRoleMultiple[] = JSON.parse(
          JSON.stringify(currentPermissions)
        );
        const foundPermissionIndex = existingPermissions.findIndex(
          (x) => x.industry.industryCode === industry && x.portal === portal
        );

        if (foundPermissionIndex === -1) return;

        existingPermissions[foundPermissionIndex].roleData = roleData;

        setCurrentPermissions([...existingPermissions]);
      }
      const industryRoleInfo = rolesInfo.find(
        (x) => x.industryCode === industry
      );

      if (
        industryRoleInfo &&
        industryRoleInfo.remanXpData &&
        industryRoleInfo.remanXpData.portalReference === ogPortalName
      ) {
        if (roleNames.length === 0) {
          setDistributorData([]);
          setSelectedDistributorIOptions([]);
          setDistributorDataSelected([]);

          const labelIndexDistributor = industryRoleLabels.findIndex(
            (x) =>
              x.industryCode === industry &&
              x.industryPortal === 'remanXPDistributors'
          );
          if (labelIndexDistributor !== -1) {
            labelData.push({
              labelIndex: labelIndexDistributor,
              labels: [],
            });
          }
        } else {
          setDistributorData(industryRoleInfo.remanXpData.distributorInfo);
        }
      } else {
        setDistributorData([]);
      }

      if (labelIndex !== -1) {
        labelData.push({
          labelIndex: labelIndex,
          labels: [...new Set(roleNames.map((x) => x.value.toString()))],
        });
      } else {
        setIndustryRoleLabels([
          ...industryRoleLabels,
          {
            industryCode: industry,
            industryPortal: portal,
            values: [...new Set(roleNames.map((x) => x.value.toString()))],
          },
        ]);
      }

      if (labelData.length > 0) {
        modifyLabels(labelData);
      }
    }
  };

  const selectIndustryBatch = (
    batchRoles: { industry: number; portal: string; roleNames: IOption[] }[]
  ): BasicPortalRoleMultiple[] => {
    if (rolesInfo !== undefined) {
      let currentPermissionsTemp: BasicPortalRoleMultiple[] = [];
      batchRoles.forEach((batchRole) => {
        const roleData: IRoleData[] = batchRole.roleNames.map<IRoleData>(
          (x) => {
            return {
              roleName: x.value.toString(),
              rolePriority:
                rolesInfo
                  .find((y) => y.industryCode === batchRole.industry)
                  ?.industryRoles.find(
                    (role) => role.roleName === x.value.toString()
                  )?.rolePriority || 0,
              roleDisplayName:
                rolesInfo
                  .find((y) => y.industryCode === batchRole.industry)
                  ?.industryRoles.find(
                    (role) => role.roleName === x.value.toString()
                  )?.roleDisplayName || x.value.toString(),
            };
          }
        );
        if (
          currentPermissionsTemp.findIndex(
            (x) =>
              x.industry.industryCode === batchRole.industry &&
              x.portal === batchRole.portal
          ) === -1
        ) {
          const newPermission: BasicPortalRoleMultiple = {
            industry: {
              industryCode: batchRole.industry,
              industryOrder: 0,
              industryText:
                rolesInfo.find((y) => y.industryCode === batchRole.industry)
                  ?.industryName || '',
            },
            portal: batchRole.portal,
            roleData: roleData,
            imxUserData: { userName: '', imxCompanyName: [], imxDbName: [] },
          };
          currentPermissionsTemp = [...currentPermissionsTemp, newPermission];
        } else {
          const existingPermissions: BasicPortalRoleMultiple[] = JSON.parse(
            JSON.stringify(currentPermissionsTemp)
          );
          const foundPermissionIndex = existingPermissions.findIndex(
            (x) =>
              x.industry.industryCode === batchRole.industry &&
              x.portal === batchRole.portal
          );

          if (foundPermissionIndex === -1) return;

          existingPermissions[foundPermissionIndex].roleData = roleData;

          currentPermissionsTemp = [...existingPermissions];
        }
      });
      return currentPermissionsTemp;
    }
    return [];
  };

  const onAddAssignRoles = (selectorRoleInfo: SelectedRoleInfo) => {
    if (
      selectedRoleInfo.find((option) =>
        isSelectedRoleInfoEqual(option, selectorRoleInfo)
      )
    )
      return;
    setSelectedRoleInfo([...selectedRoleInfo, selectorRoleInfo]);
  };

  const modifyLabels = (labelData: ILabelInfo[]) => {
    const labelCopy = JSON.parse(
      JSON.stringify(industryRoleLabels)
    ) as IIndustryRoleLabelInfo[];
    labelData.forEach((label) => {
      labelCopy[label.labelIndex].values = label.labels;
    });
    setIndustryRoleLabels(labelCopy);
  };

  const industryRoleListDom = () => {
    if (rolesInfo === undefined) return;

    return currentIndustries.map((industry) => {
      const industryState = rolesInfo.find(
        (role) => role.industryName === industry.industryText
      );

      if (industryState === undefined) return;

      return industryState.industryPortals.map(
        (portal, indexIndustryPortal) => {
          const industryRolePortal =
            industryState.industryRoles.find((role) => role.portal === portal)
              ?.industryText || portal;
          const portalRoles = industryState.industryRoles
            .filter((industryRole) => industryRole.portal === portal)
            .map<IOption>((industryRole) => {
              return {
                label: industryRole.roleDisplayName || industryRole.roleName,
                labelToShow:
                  industryRole.roleDisplayName || industryRole.roleName,
                value: industryRole.roleName,
              };
            });

          const selectedPortalRolesIOption: IOption[] =
            selectedRoles[`${industryState.industryName}_${portal}`] !=
            undefined
              ? selectedRoles[`${industryState.industryName}_${portal}`].map(
                  (x) => {
                    return {
                      label: x,
                      labelToShow: x,
                      value: x,
                    };
                  }
                )
              : [];

          const labelIndex = industryRoleLabels.findIndex(
            (x) =>
              x.industryCode === industry.industryCode &&
              x.industryPortal === industryRolePortal
          );
          const labelIndexDistributor = industryRoleLabels.findIndex(
            (x) =>
              x.industryCode === industry.industryCode &&
              x.industryPortal === 'remanXPDistributors'
          );

          const showDistributorSelector =
            industryState.remanXpData &&
            industryState.remanXpData.portalReference === portal &&
            distributorData.length > 0;

          const showLabels = (labelIndexParam) => {
            return (
              <>
                {labelIndexParam !== -1 &&
                  industryRoleLabels[labelIndexParam].values.length > 0 && (
                    <div className={style.industryLabelContainer}>
                      {industryRoleLabels[labelIndexParam].values
                        .sort((a, b) => orderByAlphabetical(a, b, true))
                        .map((x) => {
                          return (
                            <div
                              key={`${labelIndexParam}_${x}`}
                              className={style.industryLabel}
                            >
                              {x}
                            </div>
                          );
                        })}
                    </div>
                  )}
              </>
            );
          };

          return (
            <React.Fragment
              key={`${industry.industryCode}_${industryRolePortal}_${indexIndustryPortal}`}
            >
              {indexIndustryPortal === 0 && <h4>{industry.industryText}</h4>}
              <div className={style.industry}>
                <label>{industryRolePortal}</label>
                <div className={style.selectAndLabelContainer}>
                  <SelectInputMultiple
                    placeHolder={t('Role')}
                    options={(portalRoles || []).sort((a, b) =>
                      orderByAlphabetical(a.label, b.label, true)
                    )}
                    value={selectedPortalRolesIOption}
                    onSelectedItemsChanged={(e) => {
                      if (e) {
                        onSelectIndustryRole(
                          industry.industryCode,
                          industryRolePortal,
                          portal,
                          e,
                          labelIndex
                        );

                        setSelectedRoles({
                          ...selectedRoles,
                          [`${industryState.industryName}_${portal}`]: e.map(
                            (x) => x.value.toString()
                          ),
                        });
                      }
                    }}
                  />
                  {showLabels(labelIndex)}
                </div>
              </div>

              {/* REMANXP DISTRIBUTOR SELECTOR */}
              {showDistributorSelector && (
                <div className={style.industry}>
                  <label>{`${industryRolePortal} Distributors`}</label>
                  <div className={style.selectAndLabelContainer}>
                    <SelectInputMultiple
                      placeHolder={'Distributor'}
                      options={mapDistributorDataToIOption(
                        distributorData || []
                      ).sort((a, b) =>
                        orderByAlphabetical(a.label, b.label, true)
                      )}
                      value={selectedDistributorIOptions}
                      onSelectedItemsChanged={(e) => {
                        if (e) {
                          setSelectedDistributorIOptions(e);

                          const selectedDistributors: DistributorApiDTO[] = [];
                          e.forEach((distOption) => {
                            const foundDistributor = distributorData.find(
                              (ddata) =>
                                ddata.distributorAccount === distOption.value
                            );
                            if (foundDistributor)
                              selectedDistributors.push(foundDistributor);
                          });

                          setDistributorDataSelected(selectedDistributors);

                          if (labelIndexDistributor !== -1) {
                            modifyLabels([
                              {
                                labelIndex: labelIndexDistributor,
                                labels: [
                                  ...new Set(
                                    e.map((x) => x.labelToShow.toString())
                                  ),
                                ],
                              },
                            ]);
                          } else {
                            setIndustryRoleLabels([
                              ...industryRoleLabels,
                              {
                                industryCode: industry.industryCode,
                                industryPortal: 'remanXPDistributors',
                                values: [
                                  ...new Set(
                                    e.map((x) => x.labelToShow.toString())
                                  ),
                                ],
                              },
                            ]);
                          }
                        }
                      }}
                    />
                    {showLabels(labelIndexDistributor)}
                  </div>
                </div>
              )}
            </React.Fragment>
          );
        }
      );
    });
  };

  const imxRoles = () => {
    //Check to show only if industry has been selected
    if (currentIndustries.find((x) => x.industryCode === 3) === undefined) {
      if (imxData.imxCompanyName.length > 0 || imxData.imxDbName.length > 0) {
        // console.log('setting imx data to empty');
        // setImxData({
        //   userName: '',
        //   imxCompanyName: [],
        //   imxDbName: [],
        // });
      }
      return null;
    }

    const industryIMX = rolesInfo?.find(
      (x) =>
        x.imxUserData &&
        (x.imxUserData.imxDbName.length > 0 ||
          x.imxUserData.imxCompanyName.length > 0)
    );
    if (
      !industryIMX ||
      !industryIMX.imxUserData ||
      industryIMX.imxUserData.imxDbName.length === 0 ||
      industryIMX.imxUserData.imxCompanyName.length === 0
    )
      return null;

    const imxDataindustry = industryIMX.imxUserData;

    const diagnosticUnitOptions = imxDataindustry.imxCompanyName.map<IOption>(
      (x) => {
        return {
          label: x,
          labelToShow: x,
          value: x,
        };
      }
    );

    const dbNameOptions = imxDataindustry.imxDbName.map<IOption>((x) => {
      return {
        label: x,
        labelToShow: x,
        value: x,
      };
    });

    const diagnosticSelectedOptions = diagnosticUnitOptions.filter(
      (x) => imxData.imxCompanyName.indexOf(x.value.toString()) !== -1
    );

    const dbSelectedOptions = dbNameOptions.filter(
      (x) => imxData.imxDbName.indexOf(x.value.toString()) !== -1
    );

    const diagnosticUnitLabelIndex = industryRoleLabels.findIndex(
      (x) => x.industryPortal === 'Diagnostic_Unit'
    );
    const databaseLabelIndex = industryRoleLabels.findIndex(
      (x) => x.industryPortal === 'Database'
    );

    const diagnosticUnitLabels = industryRoleLabels.find(
      (x) => x.industryPortal === 'Diagnostic_Unit'
    );
    const databaseLabels = industryRoleLabels.find(
      (x) => x.industryPortal === 'Database'
    );

    return (
      <div className={style.category}>
        <h3>{t('CoMo')}</h3>
        <div className={style.imxSection}>
          <SelectInputMultiple
            placeHolder={t('Diagnostic_Unit')}
            options={diagnosticUnitOptions.sort((a, b) =>
              orderByAlphabetical(a.label, b.label, true)
            )}
            value={diagnosticSelectedOptions}
            canSelectAll={true}
            t={t}
            onSelectedItemsChanged={(selectedDiagnostics: IOption[]) => {
              setImxData({
                ...imxData,
                imxCompanyName: [
                  ...selectedDiagnostics.map((x) => x.value.toString()),
                ],
              });
              if (diagnosticUnitLabelIndex !== -1) {
                modifyLabels([
                  {
                    labelIndex: diagnosticUnitLabelIndex,
                    labels: selectedDiagnostics.map((x) => x.value.toString()),
                  },
                ]);
              } else {
                setIndustryRoleLabels([
                  ...industryRoleLabels,
                  {
                    industryCode: -1,
                    industryPortal: 'Diagnostic_Unit',
                    values: [
                      ...new Set(
                        selectedDiagnostics.map((x) => x.value.toString())
                      ),
                    ],
                  },
                ]);
              }
            }}
          />

          <SelectInputMultiple
            placeHolder={t('Database')}
            options={dbNameOptions.sort((a, b) =>
              orderByAlphabetical(a.label, b.label, true)
            )}
            value={dbSelectedOptions}
            canSelectAll={true}
            t={t}
            onSelectedItemsChanged={(selectedDatabase: IOption[]) => {
              setImxData({
                ...imxData,
                imxDbName: [...selectedDatabase.map((x) => x.value.toString())],
              });
              if (databaseLabelIndex !== -1) {
                modifyLabels([
                  {
                    labelIndex: databaseLabelIndex,
                    labels: selectedDatabase.map((x) => x.value.toString()),
                  },
                ]);
              } else {
                setIndustryRoleLabels([
                  ...industryRoleLabels,
                  {
                    industryCode: -1,
                    industryPortal: 'Database',
                    values: [
                      ...new Set(
                        selectedDatabase.map((x) => x.value.toString())
                      ),
                    ],
                  },
                ]);
              }
            }}
          />
        </div>
        {diagnosticUnitLabels && diagnosticUnitLabels.values.length > 0 && (
          <div className={style.imxLabels}>
            <label>{t('Diagnostic_Unit')}</label>
            <div className={style.selectAndLabelContainer}>
              <div className={style.industryLabelContainer}>
                {diagnosticUnitLabels.values
                  .sort((a, b) => orderByAlphabetical(a, b, true))
                  .map((x) => {
                    return <div className={style.industryLabel}>{x}</div>;
                  })}
              </div>
            </div>
          </div>
        )}
        {databaseLabels && databaseLabels.values.length > 0 && (
          <div className={style.imxLabels}>
            <label>{t('Database')}</label>
            <div className={style.selectAndLabelContainer}>
              <div className={style.industryLabelContainer}>
                {databaseLabels.values
                  .sort((a, b) => orderByAlphabetical(a, b, true))
                  .map((x) => {
                    return <div className={style.industryLabel}>{x}</div>;
                  })}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const isSaveDisabled =
    selectedRoleInfo.length === 0 ||
    apiUserRole.every((_apiUserRole) => !_apiUserRole.roleName);

  return (
    <div className={style.content}>
      <div className={style.category}>
        <h3>{t('Users')}</h3>
        <div className={style.users}>
          {assignUserRoles.map((_user?: User) => _user?.userName).join(', ')}
        </div>
      </div>

      <div className={style.category}>
        <h3>{t('Roles')}</h3>
        <div className={style.inputRowContainer}>
          <UserManagementAssignRolesSelector onAdd={onAddAssignRoles} />
        </div>
        <UserManagementAssignRolesDataTable
          roles={selectedRoleInfo}
          onDeleteRole={_onDeleteRole}
        />
      </div>
      {imxRoles()}
      <div className={style.category}>
        <h3>{t('Industries_roles')}</h3>
        <div className={style.access}>{industryRoleListDom()}</div>
      </div>
      <div className={style.navigation}>
        <ButtonPrimary onClick={onClickCancel} outline>
          {t('Cancel')}
        </ButtonPrimary>
        <ButtonPrimary onClick={onClickSubmit} disabled={isSaveDisabled}>
          {t('Save')}
        </ButtonPrimary>
      </div>
    </div>
  );
};

export default UserManagementAssignRoles;
